<template>
    <flex-container :gap="Size.lg">
        <flex-container justify-content="space-between" align-items="center">
            <flex-container size="auto" :gap="Size.xs">
                <text-heading>
                    Brochure genereren
                </text-heading>
            </flex-container>

            <flex-container size="auto" justify-content="flex-end">
                <button-outline @click="backToLister">
                    Annuleren
                </button-outline>

                <button-block @loading="generateBrochure">
                    Brochure genereren
                </button-block>
            </flex-container>
        </flex-container>
    </flex-container>

    <horizontal-line />

    <flex-container>
        <flex-container :size="10">
            <text-heading style="margin-bottom: .5rem">
                Instellingen
            </text-heading>

            <card-normal>
                <flex-container no-wrap>
                    <flex-container :size="3">
                        <input-group v-model:model-value="formData.template">
                            <template #label>
                                <input-label>
                                    Template
                                </input-label>
                            </template>
                            <template #input>
                                <select-input
                                    label-key-name="name"
                                    value-key-name="id"
                                    :items="availableTemplates"
                                />
                            </template>
                        </input-group>
                    </flex-container>
                    <flex-container :size="3">
                        <input-group v-model:model-value="formData.team">
                            <template #label>
                                <input-label>
                                    Afzender
                                </input-label>
                            </template>
                            <template #input>
                                <select-input
                                    label-key-name="name"
                                    value-key-name="id"
                                    :items="availableTeams"
                                />
                            </template>
                        </input-group>
                    </flex-container>
                    <flex-container :size="5">
                        <input-group :model-value="wonenObjectLabel">
                            <template #label>
                                <input-label>
                                    Object
                                </input-label>
                            </template>
                            <template #input>
                                <select-input
                                    :items="[]"
                                    @click="openWonenObjectList"
                                />
                            </template>
                        </input-group>
                    </flex-container>
                </flex-container>
            </card-normal>

            <flex-container justify-content="center">
                <button-block @loading="generatePreview">
                    <svg-icon :icon="IconRefresh" />
                    <span>Voorbeeld</span>
                </button-block>
            </flex-container>

            <horizontal-line />

            <text-heading style="margin-bottom: .5rem">
                Brochure bewerken
            </text-heading>

            <card-normal>
                <flex-container>
                    <text-heading sub>
                        Omschrijving
                    </text-heading>
                </flex-container>

                <template #sections>
                    <card-section area="card-footer">
                        <input-group v-model="formData.aanbiedingstekst">
                            <template #input>
                                <rich-text-area-input
                                    size="lg"
                                    toolbar="extended"
                                    :mentionables="[]"
                                />
                            </template>
                        </input-group>
                    </card-section>
                </template>
            </card-normal>
        </flex-container>

        <flex-container :size="10" style="flex: 0 0 640px;">
            <card-normal>
                <iframe
                    ref="previewIframe"
                    title="Preview"
                    :srcdoc="formattedPreview"
                    style="width: 100%; height: 896px; border: none;"
                />
            </card-normal>
        </flex-container>
    </flex-container>

    <modal-dialog
        ref="selectWonenObjectModal"
        label="Kies wonen object"
        description="Kies wonen object"
        without-padding
        :size="5"
    >
        <card-normal>
            <flex-container
                no-wrap
                align-items="flex-end"
                style="margin-bottom: 1rem"
            >
                <flex-container>
                    <input-group v-model="wonenObjectSearch">
                        <template #label>
                            <input-label>
                                Woning zoeken op adres
                            </input-label>
                        </template>
                        <template #input>
                            <text-input>
                                <template #prefix>
                                    <svg-icon :icon="IconSearch" :size="IconSize.sm" />
                                </template>
                            </text-input>
                        </template>
                    </input-group>
                </flex-container>
            </flex-container>

            <div v-if="wonenObjectResults.length > 0">
                <text-heading sub style="margin: 1rem 0">
                    {{ wonenObjectResults.length }} resultaten gevonden voor '{{ wonenObjectSearch }}'
                </text-heading>

                <list-container>
                    <list-row
                        v-for="result in wonenObjectResults"
                        :key="result.id"
                        style="cursor: pointer"
                        @click="selectWonenObject(result)"
                    >
                        <list-cell-text>
                            <template #content>
                                <text-decorator>{{ result.adres.straat }} {{ result.adres.huisnummer }}{{ result.adres.huisnummertoevoeging }}</text-decorator>
                                <text-description>{{ result.adres.postcode }} {{ result.adres.plaats }}</text-description>
                            </template>
                        </list-cell-text>

                        <!-- list-cell-icon heeft teveel padding rechts en is niet te overriden met style -->
                        <list-cell>
                            <flex-container
                                align-content="center"
                                justify-content="center"
                                style="height: 100%"
                            >
                                <svg-icon
                                    v-if="selectedWonenObject?.id === result.id"
                                    :icon="IconChecked"
                                    :color="Color.brandGreen"
                                />
                            </flex-container>
                        </list-cell>
                    </list-row>
                </list-container>
            </div>

            <template #sections>
                <card-section area="card-header" :color="Color.none">
                    <text-decorator bold :size="FontSize.lg">
                        Object zoeken
                    </text-decorator>
                </card-section>

                <card-section area="card-footer">
                    <flex-container justify-content="space-between">
                        <button-outline @click="closeWonenObjectList">
                            Annuleren
                        </button-outline>

                        <button-block :disabled="!selectedWonenObject" @loading="applySelectedWonenObject">
                            Kiezen
                        </button-block>
                    </flex-container>
                </card-section>
            </template>
        </card-normal>
    </modal-dialog>
</template>

<script setup lang="ts">
    import { ButtonState, type ButtonStateChangedEvent, Color, type ExposedOverlay, FontSize, IconSize, Size } from '@keyvue/one-ui';
    import { BrochureId, CreateBrochureCommand, PreviewBrochureCommand, Team, Template } from '@keyvue/one-api-client';
    import { RealworksAPI } from '@keyvue/one-api-client-legacy';

    import { computed, reactive, ref, watch } from 'vue';
    import { useRouter } from 'vue-router';
    import useClient from '@composables/useClient';
    import useNotify from '@composables/useNotify';

    import IconRefresh from '@keyvue/one-material-icons/icons/refresh.svg?component';
    import IconSearch from '@keyvue/one-material-icons/icons/search.svg?component';
    import IconChecked from '@keyvue/one-material-icons/icons/check.svg?component';

    const router = useRouter();
    const { notifySaving, notifySaved, notifyError } = useNotify();
    const { client, getReactiveCollection, currentUser, currentCompany } = useClient();
    if (!currentUser.value) throw new Error('No user found');

    const templateCollection = await getReactiveCollection(Template, { active: true });
    const userTeams = await client.getSubResources(currentUser.value, Team, 'teams');

    const availableTemplates = computed(() => {
        const templates = templateCollection.data.value;
        templates.sort((a, b) => (a.updatedAt > b.updatedAt ? 1 : -1));

        return templates.filter((template) => template.active);
    });
    const availableTeams = computed(() => userTeams.data);

    const wonenObjectSearch = ref<string>('');
    const wonenObjectResults = ref<any[]>([]);
    const selectWonenObjectModal = ref<ExposedOverlay>();

    const formData = reactive<{
        template: Template | null;
        team: Team | null;
        wonenObject: any | null;
        aanbiedingstekst: string;
    }>({
        template: null,
        team: null,
        wonenObject: null,
        aanbiedingstekst: '',
    });

    const openWonenObjectList = () => {
        selectWonenObjectModal.value?.openOverlay();
    };

    const closeWonenObjectList = () => {
        selectedWonenObject.value = null;
        selectWonenObjectModal.value?.closeOverlay();
    };

    const selectedWonenObject = ref<any | null>(null);
    const selectWonenObject = (object: any) => {
        selectedWonenObject.value = object;
    };

    const applySelectedWonenObject = (event: ButtonStateChangedEvent) => {
        formData.wonenObject = selectedWonenObject.value;
        const aanbiedingstekst = selectedWonenObject.value.teksten.aanbiedingstekst
            .replaceAll(/(\r\n|\r|\n)/g, '</p><p>');
        formData.aanbiedingstekst = `<p>${aanbiedingstekst}</p>`;

        event.button.setState(ButtonState.completed);
        selectWonenObjectModal.value?.closeOverlay();
    };

    const wonenObjectLabel = computed(() => {
        if (!formData.wonenObject) {
            return 'Kies object';
        }

        return `${formData.wonenObject.adres.straat} ${formData.wonenObject.adres.huisnummer}${formData.wonenObject.adres.huisnummertoevoeging ?? ''}, ${formData.wonenObject.adres.postcode} ${formData.wonenObject.adres.plaats}`;
    });

    watch(wonenObjectSearch, async (value) => {
        if (value.length < 3) {
            wonenObjectResults.value = [];
            return;
        }

        if (!currentCompany.value?.id.value) {
            return;
        }

        wonenObjectResults.value = await RealworksAPI.searchWonenObjects(
            currentCompany.value?.id.value,
            wonenObjectSearch.value,
        );

        wonenObjectResults.value = wonenObjectResults.value.slice(0, 5);
    });

    const backToLister = () => {
        router.push({ name: 'documentList' });
    };

    const generateBrochure = async (event: ButtonStateChangedEvent) => {
        if (!formData.template || !formData.team || !formData.wonenObject || !currentUser.value) {
            notifyError(new Error('Niet alle velden zijn ingevuld'));
            event.button.setState(ButtonState.content);
            return;
        }

        try {
            notifySaving();
            await client.dispatch(new CreateBrochureCommand(
                BrochureId.generate().value,
                formData.template.id.value,
                formData.team.id.value,
                formData.wonenObject.id,
                wonenObjectLabel.value,
                { aanbiedingstekst: formData.aanbiedingstekst },
                currentUser.value?.id.value,
                new Date(),
                new Date(),
            ));
            notifySaved();
            backToLister();
        } catch (error) {
            notifyError(error);
        } finally {
            event.button.setState(ButtonState.completed);
        }
    };

    const availableBaseTemplates = computed(() => [
        { value: '667c0e84-d459-4f01-94e3-f11c238c881e', label: 'Brochure - Liggend', orientation: 'landscape' },
        { value: 'f9b655c1-1363-4664-b5a9-4d3d86901ef9', label: 'Brochure - Staand', orientation: 'portrait' },
    ]);

    const orientation = computed(() => {
        const selectedTemplate = availableBaseTemplates.value.find((availableTemplate) => availableTemplate.value === formData.template?.template);
        if (selectedTemplate) {
            return selectedTemplate.orientation;
        }

        return undefined;
    });

    const previewIframe = ref<HTMLIFrameElement>();

    const previewZoom = ref(1.0);
    const recalculatePreviewZoom = () => {
        let pageWidth = 793;
        if (orientation.value === 'landscape') {
            pageWidth = 1120;
        }

        // clientWidth - left margin - right margin - scrollbar width
        const iframeWidth = (previewIframe.value?.clientWidth ?? pageWidth) - 16 - 16 - 24;

        previewZoom.value = Math.min(1, iframeWidth / pageWidth);
    };

    const preview = ref<string>('');
    const generatePreview = async (event: ButtonStateChangedEvent) => {
        if (!formData.template || !formData.team || !formData.wonenObject) {
            notifyError(new Error('Niet alle velden zijn ingevuld'));
            event.button.setState(ButtonState.content);
            return;
        }

        preview.value = '';
        recalculatePreviewZoom();

        try {
            preview.value = await client.dispatch(
                new PreviewBrochureCommand(
                    formData.template.id.value,
                    formData.team.id.value,
                    formData.wonenObject.id,
                    { aanbiedingstekst: formData.aanbiedingstekst },
                ),
            );
        } catch (error) {
            notifyError(error);
        } finally {
            event.button.setState(ButtonState.completed);
        }
    };

    const formattedPreview = computed(() => `
        ${preview.value}

        <style>
            html {
                -moz-transform: scale(${previewZoom.value});
                -moz-transform-origin: 0 0;
                -o-transform: scale(${previewZoom.value});
                -o-transform-origin: 0 0;
                -webkit-transform: scale(${previewZoom.value});
                -webkit-transform-origin: 0 0;
            }
        </style>
    `);
</script>

<style scoped lang="scss">
.search-results {
    margin-top: size(md);
    background-color: white;

    .search-result {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: size(sm);
        padding: size(sm) 0;

        .button-block {
            opacity: 0;
        }

        &:hover .button-block {
            opacity: 1;
        }

        &:not(:last-child) {
            border-bottom: 1px solid #dadada;
        }
    }
}
</style>
